import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createSnProductMapping } from '#/api/aftersalesMgmt/snProductMapping/create';
import { fetchSnProductMappingDetail } from '#/api/aftersalesMgmt/snProductMapping/fetchDetail';
import { updateSnProductMapping } from '#/api/aftersalesMgmt/snProductMapping/update';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import LookupRefreshField from '#/modules/commonComponents/form/LookupRefreshField';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import SnProductMappingEditInfoCard from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingEditInfoCard';
import SnProductMappingStatusBadge from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingStatusBadge';
import {
  getSnProductMappingApiErrorMessage,
  isSnProductMappingVersionConflict,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/apiError';
import {
  buildSnProductMappingWriteValues,
  mapSnProductMappingDetailToForm,
  SN_PRODUCT_MAPPING_FORM_DEFAULTS,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/formValues';
import {
  composeProductCode,
  listCustomerSnPrefixes,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/productCode';
import {
  canEditSnProductMappingIdentity,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/snProductMappingStatus';
import {
  SN_PRODUCT_MAPPING_FIELD_LABELS,
  snProductMappingFormSchema,
} from '#/modules/crmMgmt/aftersales/snProductMapping/schemas/snProductMappingSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  customerDetailQueryKey,
  lookupQueryKey,
  snProductMappingDetailQueryKey,
  snProductMappingListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const CUSTOMER_DETAIL_QUERY_KEY_PREFIX = ['erp', 'customer', 'detail'];

const CreateEditSnProductMappingPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const hydratedStampRef = useRef('');
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: snProductMappingDetailQueryKey(id),
    queryFn: () => fetchSnProductMappingDetail(id),
    enabled: Boolean(id),
    retry: false,
  });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(snProductMappingFormSchema),
    defaultValues: SN_PRODUCT_MAPPING_FORM_DEFAULTS,
  });

  const detail = detailQuery.data?.data;
  const formCustomerId = useWatch({ control, name: 'customerId' });
  const prefix = useWatch({ control, name: 'prefix' });
  const codeDigits = useWatch({ control, name: 'codeDigits' });
  const identityLocked = Boolean(id) && !canEditSnProductMappingIdentity(detail?.status);
  const lookupCustomerId = formCustomerId || (!isDirty ? (detail?.customerId || '') : '');

  const customerLookupQuery = useQuery({
    queryKey: customerDetailQueryKey(lookupCustomerId),
    queryFn: () => fetchCustomerDetail(lookupCustomerId),
    enabled: Boolean(lookupCustomerId),
    retry: false,
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createSnProductMapping,
    updateFn: updateSnProductMapping,
    buildPayload: (values) => buildSnProductMappingWriteValues(values, updatedAtRef.current),
    listQueryKeyPrefix: snProductMappingListQueryKeyPrefix,
    detailQueryKey: snProductMappingDetailQueryKey,
    getDetailPath: ROUTES.snProductMappingDetail,
    afterUpdateNavigateTo: id ? ROUTES.snProductMappingDetail(id) : null,
    getErrorMessage: (error) => getSnProductMappingApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isSnProductMappingVersionConflict,
    onVersionConflict: () => {
      hydratedStampRef.current = '';
      detailQuery.refetch();
    },
  });

  const prefixes = useMemo(
    () => listCustomerSnPrefixes(customerLookupQuery.data?.data),
    [customerLookupQuery.data],
  );

  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(
      customerOptionsQuery.data ?? [],
      customerLookupQuery.data?.data,
    );
    return [{ value: '', label: '不指定客戶' }, ...options];
  }, [customerOptionsQuery.data, customerLookupQuery.data]);

  const prefixOptions = useMemo(() => prefixes.map((code) => ({
    value: code,
    label: code.length === 2 ? `兩碼（${code}）` : `三碼（${code}）`,
  })), [prefixes]);

  useEffect(() => {
    if (!detail) {
      return;
    }
    const needsCustomer = Boolean(detail.customerId) && canEditSnProductMappingIdentity(detail.status);
    if (needsCustomer && !customerLookupQuery.isFetched) {
      return;
    }
    const stamp = `${detail.id}:${detail.record?.updatedAt ?? ''}`;
    if (hydratedStampRef.current === stamp) {
      return;
    }
    hydratedStampRef.current = stamp;
    updatedAtRef.current = detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapSnProductMappingDetailToForm(detail, prefixes));
  }, [
    detail,
    prefixes,
    customerLookupQuery.isFetched,
    reset,
    resetValidationSummary,
  ]);

  useEffect(() => {
    if (identityLocked || !isDirty || !formCustomerId) {
      return;
    }
    if (customerLookupQuery.isFetching || prefix || prefixes.length !== 1) {
      return;
    }
    setValue('prefix', prefixes[0]);
  }, [
    identityLocked,
    isDirty,
    formCustomerId,
    prefix,
    prefixes,
    customerLookupQuery.isFetching,
    setValue,
  ]);

  const composedCode = composeProductCode(prefix, codeDigits);
  const showMissingPrefix = Boolean(formCustomerId)
    && !identityLocked
    && customerLookupQuery.isFetched
    && !customerLookupQuery.isFetching
    && prefixes.length === 0;
  const pageTitle = isEdit ? '編輯 物料序號' : '新增 物料序號';
  const isPageLoading = isEdit && (detailQuery.isLoading || (
    Boolean(detail?.customerId)
    && canEditSnProductMappingIdentity(detail?.status)
    && customerLookupQuery.isLoading
  ));

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save({
      ...values,
      identityLocked,
    });
  }

  function handleCustomerChange() {
    setValue('prefix', '');
    setValue('codeDigits', '');
  }

  async function handleRefreshCustomers() {
    setValue('customerId', '', { shouldDirty: true, shouldValidate: false });
    handleCustomerChange();
    await queryClient.invalidateQueries({
      queryKey: lookupQueryKey('customerSelect'),
    });
    await queryClient.invalidateQueries({
      queryKey: CUSTOMER_DETAIL_QUERY_KEY_PREFIX,
    });
    const result = await customerOptionsQuery.refetch();
    if (result.isError) {
      toast.error('更新失敗，請稍後再試');
      return;
    }
    toast.success('客戶列表已更新，請重新選擇客戶');
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl">
          <AftersalesOpsPageHeader
            page="snProductMapping"
            variant="form"
            title={pageTitle}
            statusSlot={
              isEdit && detail?.status
                ? <SnProductMappingStatusBadge status={detail.status} />
                : null
            }
            trailing={id ? (
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.snProductMappingDetail(id)}>返回詳情</Link>
              </Button>
            ) : null}
          />

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}

          {isEdit && detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getSnProductMappingApiErrorMessage(detailQuery.error, '找不到 物料序號')}
            </p>
          ) : null}

          {!isPageLoading && !(isEdit && detailQuery.isError) ? (
            <>
              {isEdit && detail ? (
                <SnProductMappingEditInfoCard
                  detail={detail}
                  customer={customerLookupQuery.data?.data}
                  customerLoading={customerLookupQuery.isLoading}
                  identityLocked={identityLocked}
                />
              ) : null}
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={SN_PRODUCT_MAPPING_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <section className="pb-5">
                    {!identityLocked ? (
                      <>
                        <div className="form-block">
                          <h2 className="form-block__title">對照資料</h2>
                        </div>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <LookupRefreshField
                            onRefresh={handleRefreshCustomers}
                            isRefreshing={customerOptionsQuery.isFetching}
                            ariaLabel="重新載入客戶列表"
                          >
                            <FloatingSelect
                              id="customerId"
                              label="客戶（可以不選）"
                              options={customerOptions}
                              control={control}
                              errors={errors}
                              isClearable
                              isDisabled={customerOptionsQuery.isLoading}
                              noOptionsMessage="無可用客戶"
                              onChange={handleCustomerChange}
                            />
                          </LookupRefreshField>
                          {formCustomerId ? (
                            <FloatingSelect
                              id="prefix"
                              label="客戶代碼"
                              options={prefixOptions}
                              control={control}
                              errors={errors}
                              allowEmpty
                              isDisabled={prefixes.length === 0}
                              noOptionsMessage="此客戶沒有 SN 前綴"
                            />
                          ) : (
                            <FloatingInput
                              id="productCode"
                              label="產品代碼"
                              register={register}
                              errors={errors}
                            />
                          )}
                          {formCustomerId ? (
                            <FloatingInput
                              id="codeDigits"
                              label="數字段（3～4 碼）"
                              register={register}
                              errors={errors}
                            />
                          ) : null}
                          {formCustomerId ? (
                            <FloatingInput
                              id="composedProductCode"
                              label="產品代碼"
                              value={composedCode}
                              readOnly
                            />
                          ) : null}
                        </div>
                        {showMissingPrefix ? (
                          <p className="mt-3 text-sm text-destructive">
                        此客戶尚未設定 SN 前綴，不能帶著這個客戶儲存。
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    <div className={identityLocked ? 'form-block' : 'form-block mt-6'}>
                      <h2 className="form-block__title">產品資訊</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-y-1">
                      <FloatingInput
                        id="productName"
                        label="產品名稱"
                        register={register}
                        errors={errors}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={3}
                      />
                    </div>
                  </section>
                </div>
                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={id ? ROUTES.snProductMappingDetail(id) : ROUTES.snProductMapping}>
                      取消
                    </Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending || showMissingPrefix || customerLookupQuery.isFetching}
                  >
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditSnProductMappingPage;
